import { Puzzle } from '../types/puzzle'

const createPuzzle = (
    index: number,
    title: string,
    cells: string[][],
    cluesData: { a: { n: number, c: string, a: string }[], d: { n: number, c: string, a: string }[] }
): Puzzle => {
    const size = 5
    const gridCells = cells
    const blocked: [number, number][] = []

    // Auto-detect blocked
    for (let r = 0; r < size; r++) {
        for (let c = 0; c < size; c++) {
            if (!gridCells[r][c]) blocked.push([r, c])
        }
    }

    const mapClues = (list: any[], dir: 'across' | 'down') => list.map(item => {
        // Find start cell
        let sr = 0, sc = 0
        if (dir === 'across') {
            // Find row with this answer
            // This is expenisve to compute blindly, so we expect data to be valid or we just pass defaults.
            // To simplify manual entry, I'll just pass 0,0 and let the game logic handle it or 
            // actually, the game logic needs 'startCell'. 
            // Let's simplified: we assume the provided grid matches.
            // For 5x5, we can just find the matching text sequence.

            for (let r = 0; r < size; r++) {
                const rowStr = gridCells[r].map(x => x || ' ').join('')
                const idx = rowStr.indexOf(item.a)
                if (idx >= 0) { sr = r; sc = idx; break; }
            }
        } else {
            // vertical
            for (let c = 0; c < size; c++) {
                const colStr = gridCells.map(row => row[c] || ' ').join('')
                const idx = colStr.indexOf(item.a)
                if (idx >= 0) { sr = idx; sc = c; break; }
            }
        }

        return {
            number: item.n,
            clue: item.c,
            answer: item.a,
            row: dir === 'across' ? sr : undefined,
            col: dir === 'down' ? sc : undefined,
            startCell: [sr, sc] as [number, number]
        }
    })

    return {
        id: `puz-${String(index + 1).padStart(3, '0')}`,
        dayNumber: index,
        type: 'crossword',
        difficulty: 'easy',
        title,
        theme: 'Daily',
        author: 'BlazeTeam',
        createdAt: new Date().toISOString(),
        grid: { size, cells: gridCells, blocked },
        clues: {
            across: mapClues(cluesData.a, 'across'),
            down: mapClues(cluesData.d, 'down')
        }
    }
}

const BASE_PUZZLES: Puzzle[] = [
    createPuzzle(0, "Fruit Salad", [
        ['A', 'P', 'P', 'L', 'E'],
        ['B', 'E', 'A', 'R', 'S'],
        ['O', 'U', 'T', 'E', 'R'],
        ['U', 'S', 'E', 'D', 'S'],
        ['T', 'H', 'E', 'M', 'E']
    ], {
        a: [{ n: 1, c: 'iPhone maker', a: 'APPLE' }, { n: 6, c: 'Forest animals', a: 'BEARS' }, { n: 7, c: 'External', a: 'OUTER' }, { n: 8, c: 'Used before', a: 'USEDS' }, { n: 9, c: 'Central topic', a: 'THEME' }],
        d: [{ n: 1, c: 'Approximately', a: 'ABOUT' }, { n: 2, c: 'Brief stop', a: 'PAUSE' }, { n: 3, c: 'Liver paste', a: 'PATE' }, { n: 4, c: 'Graphite stick', a: 'LEAD' }, { n: 5, c: 'Short paper', a: 'ESSAY' }]
    }),

    // 1. Basic Blocked
    createPuzzle(1, "Cat & Mouse", [
        ['C', 'A', 'T', 'S', ''],
        ['L', 'I', 'O', 'N', 'S'],
        ['A', 'N', 'T', 'S', ''],
        ['M', 'I', 'C', 'E', ''],
        ['S', 'N', 'A', 'K', 'E']
    ], {
        a: [{ n: 1, c: 'Meowing pets', a: 'CATS' }, { n: 4, c: 'Safari kings', a: 'LIONS' }, { n: 6, c: 'Hill builders', a: 'ANTS' }, { n: 7, c: 'Cheese eaters', a: 'MICE' }, { n: 8, c: 'Long reptile', a: 'SNAKE' }],
        d: [{ n: 1, c: 'Seafood shell', a: 'CLAMS' }, { n: 2, c: 'Home', a: 'IN' }, { n: 3, c: 'Sum', a: 'TOT' }, { n: 5, c: 'Move quietly', a: 'SNEAK' }]
    }),

    // 2. Beach
    createPuzzle(2, "Beach Day", [
        ['S', 'A', 'N', 'D', ''],
        ['W', 'A', 'V', 'E', 'S'],
        ['I', 'S', 'L', 'E', ''],
        ['M', 'E', 'L', 'T', 'S'],
        ['', '', 'S', 'U', 'N']
    ], {
        a: [{ n: 1, c: 'Beach powder', a: 'SAND' }, { n: 4, c: 'Ocean swells', a: 'WAVES' }, { n: 6, c: 'Small island', a: 'ISLE' }, { n: 7, c: 'Liquifies', a: 'MELTS' }, { n: 8, c: 'Sky star', a: 'SUN' }],
        d: [{ n: 1, c: 'Lap pool activity', a: 'SWIM' }, { n: 2, c: 'Vase', a: 'VASE' }, { n: 3, c: 'Invalid', a: 'NULL' }, { n: 4, c: 'Compass point', a: 'WEST' }, { n: 5, c: 'Compass point', a: 'EAST' }]
    }),

    createPuzzle(3, "Colors", [
        ['R', 'E', 'D', '', ''],
        ['B', 'L', 'U', 'E', ''],
        ['P', 'I', 'N', 'K', ''],
        ['G', 'R', 'E', 'E', 'N'],
        ['G', 'O', 'L', 'D', '']
    ], {
        a: [{ n: 1, c: 'Stop color', a: 'RED' }, { n: 3, c: 'Sky hue', a: 'BLUE' }, { n: 5, c: 'Light red', a: 'PINK' }, { n: 6, c: 'Grass hue', a: 'GREEN' }, { n: 8, c: 'Medal metal', a: 'GOLD' }],
        d: [{ n: 1, c: 'Music genre R&_', a: 'RB' }, { n: 2, c: 'Elf', a: 'ELIGO' }, { n: 4, c: 'Ending', a: 'END' }]
    }),


    createPuzzle(4, "Body Parts", [
        ['L', 'E', 'G', 'S', ''],
        ['E', 'Y', 'E', 'S', ''],
        ['H', 'A', 'N', 'D', 'S'],
        ['A', 'R', 'M', 'S', ''],
        ['', '', '', '', '']
    ], {
        a: [
            { n: 1, c: 'Walk with these', a: 'LEGS' },
            { n: 4, c: 'See with these', a: 'EYES' },
            { n: 6, c: 'Hold with these', a: 'HANDS' },
            { n: 7, c: 'Wave with these', a: 'ARMS' }
        ],
        d: [
            { n: 1, c: 'Jump', a: 'LEAP' },
            { n: 2, c: 'Yolk holder', a: 'EGG' },
            { n: 3, c: 'Gaze', a: 'GYES' },
            { n: 5, c: 'Plural suffix', a: 'SSS' }
        ]
    }),

    createPuzzle(5, "Four Square", [
        ['S', 'T', 'A', 'R', ''],
        ['T', 'I', 'M', 'E', ''],
        ['A', 'R', 'E', 'A', ''],
        ['R', 'E', 'A', 'D', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Night light', a: 'STAR' }, { n: 4, c: 'Clock info', a: 'TIME' }, { n: 6, c: 'Zone', a: 'AREA' }, { n: 7, c: 'Scan book', a: 'READ' }], d: [{ n: 1, c: 'Celebrity', a: 'STAR' }, { n: 2, c: 'Rubber wheel', a: 'TIRE' }, { n: 3, c: 'Friend (Fr.)', a: 'AMIE' }, { n: 5, c: 'Study', a: 'READ' }] }),

    createPuzzle(6, "Pets", [
        ['B', 'I', 'R', 'D', ''],
        ['O', 'D', 'O', 'R', ''],
        ['A', 'L', 'O', 'E', ''],
        ['T', 'E', 'N', 'T', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Flying animal', a: 'BIRD' }, { n: 5, c: 'Smell', a: 'ODOR' }, { n: 6, c: 'Healing plant', a: 'ALOE' }, { n: 7, c: 'Camping gear', a: 'TENT' }], d: [{ n: 1, c: 'Sail vessel', a: 'BOAT' }, { n: 2, c: 'Lazy', a: 'IDLE' }, { n: 3, c: 'Fastening', a: 'ROON' }, { n: 4, c: 'Dress', a: 'DRET' }] }),

    createPuzzle(7, "Fast Food", [
        ['S', 'O', 'D', 'A', ''],
        ['T', 'R', 'A', 'Y', ''],
        ['E', 'A', 'T', 'S', ''],
        ['W', 'R', 'A', 'P', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Fizzy drink', a: 'SODA' }, { n: 5, c: 'Cafeteria plate', a: 'TRAY' }, { n: 6, c: 'Dines', a: 'EATS' }, { n: 7, c: 'Tortilla sandwich', a: 'WRAP' }], d: [{ n: 1, c: 'Cook slowly', a: 'STEW' }, { n: 2, c: 'Lion sound', a: 'ROAR' }, { n: 3, c: 'Factor', a: 'DATA' }, { n: 4, c: 'Yes vote', a: 'AYS' }] }),

    createPuzzle(8, "Tech", [
        ['D', 'A', 'T', 'A', ''],
        ['E', 'X', 'I', 'T', ''],
        ['M', 'E', 'M', 'O', ''],
        ['O', 'N', 'E', 'S', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Information', a: 'DATA' }, { n: 5, c: 'Leave', a: 'EXIT' }, { n: 6, c: 'Note', a: 'MEMO' }, { n: 7, c: 'Binary digit', a: 'ONES' }], d: [{ n: 1, c: 'Trial', a: 'DEMO' }, { n: 2, c: 'Graph axis', a: 'AXON' }, { n: 3, c: 'Clock sound', a: 'TIME' }, { n: 4, c: 'Tiny particles', a: 'ATOS' }] }),

    createPuzzle(9, "Space", [
        ['M', 'A', 'R', 'S', ''],
        ['O', 'R', 'B', 'I', 'T'],
        ['O', 'E', 'M', 'S', ''],
        ['N', 'A', 'S', 'A', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Red Planet', a: 'MARS' }, { n: 4, c: 'Path around', a: 'ORBIT' }, { n: 6, c: 'Book maker (abbr)', a: 'OEM' }, { n: 7, c: 'Space Agency', a: 'NASA' }], d: [{ n: 1, c: 'Night satellite', a: 'MOON' }, { n: 2, c: 'Region', a: 'AREA' }, { n: 3, c: 'Rims', a: 'RIMS' }, { n: 5, c: 'Sit', a: 'SIT' }] }),

    createPuzzle(10, "Numbers", [
        ['O', 'N', 'E', '', ''],
        ['T', 'W', 'O', '', ''],
        ['T', 'H', 'R', 'E', 'E'],
        ['F', 'O', 'U', 'R', ''],
        ['F', 'I', 'V', 'E', '']
    ], { a: [{ n: 1, c: '1', a: 'ONE' }, { n: 3, c: '2', a: 'TWO' }, { n: 5, c: '3', a: 'THREE' }, { n: 7, c: '4', a: 'FOUR' }, { n: 8, c: '5', a: 'FIVE' }], d: [{ n: 1, c: 'Not False', a: 'TRUE' }, { n: 2, c: 'Current', a: 'NOW' }, { n: 4, c: 'Unit of time', a: 'HOUR' }, { n: 6, c: 'Revolutions', a: 'REV' }] }),

    createPuzzle(11, "Family", [
        ['M', 'O', 'M', '', ''],
        ['D', 'A', 'D', '', ''],
        ['A', 'U', 'N', 'T', ''],
        ['S', 'O', 'N', '', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Mother', a: 'MOM' }, { n: 3, c: 'Father', a: 'DAD' }, { n: 5, c: 'Parent\'s sister', a: 'AUNT' }, { n: 7, c: 'Male child', a: 'SON' }], d: [{ n: 1, c: 'Crazy', a: 'MAD' }, { n: 2, c: 'Planet', a: 'OAU' }, { n: 4, c: 'DNA', a: 'DNA' }] }),

    createPuzzle(12, "Directions", [
        ['N', 'O', 'R', 'T', 'H'],
        ['E', 'A', 'S', 'T', ''],
        ['W', 'E', 'S', 'T', ''],
        ['S', 'O', 'U', 'T', 'H'],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Up on map', a: 'NORTH' }, { n: 5, c: 'Sun rise', a: 'EAST' }, { n: 6, c: 'Sun set', a: 'WEST' }, { n: 7, c: 'Down on map', a: 'SOUTH' }], d: [{ n: 1, c: 'Current news', a: 'NEWS' }, { n: 2, c: 'Propel boat', a: 'OAR' }, { n: 3, c: 'Support', a: 'RES' }, { n: 4, c: 'Examinations', a: 'TEST' }] }),

    createPuzzle(13, "Garden", [
        ['R', 'O', 'S', 'E', ''],
        ['O', 'V', 'E', 'N', ''],
        ['T', 'R', 'E', 'E', ''],
        ['S', 'E', 'E', 'D', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Love flower', a: 'ROSE' }, { n: 4, c: 'Baking appliance', a: 'OVEN' }, { n: 6, c: 'Tall plant', a: 'TREE' }, { n: 7, c: 'Plant starter', a: 'SEED' }], d: [{ n: 1, c: 'Decompose', a: 'ROTS' }, { n: 2, c: 'Finished', a: 'OVER' }, { n: 3, c: 'Look', a: 'SEE' }, { n: 5, c: 'Requirement', a: 'NEED' }] }),

    createPuzzle(14, "Action", [
        ['R', 'U', 'N', '', ''],
        ['J', 'U', 'M', 'P', ''],
        ['W', 'A', 'L', 'K', ''],
        ['S', 'I', 'T', '', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Sprint', a: 'RUN' }, { n: 3, c: 'Hop', a: 'JUMP' }, { n: 5, c: 'Stroll', a: 'WALK' }, { n: 6, c: 'Chair action', a: 'SIT' }], d: [{ n: 1, c: 'Use oars', a: 'ROW' }, { n: 2, c: 'Usually', a: 'USUAL' }, { n: 4, c: 'Parking place', a: 'PK' }] }),

    createPuzzle(15, "Feelings", [
        ['L', 'O', 'V', 'E', ''],
        ['H', 'A', 'T', 'E', ''],
        ['J', 'O', 'Y', '', ''],
        ['S', 'A', 'D', '', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Heart emotion', a: 'LOVE' }, { n: 4, c: 'Despise', a: 'HATE' }, { n: 6, c: 'Happiness', a: 'JOY' }, { n: 7, c: 'Unhappy', a: 'SAD' }], d: [{ n: 1, c: 'Laugh Out Loud', a: 'LOL' }] }),

    createPuzzle(16, "School", [
        ['B', 'O', 'O', 'K', ''],
        ['P', 'E', 'N', '', ''],
        ['M', 'A', 'T', 'H', ''],
        ['A', 'R', 'T', '', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Reading material', a: 'BOOK' }, { n: 4, c: 'Writing tool', a: 'PEN' }, { n: 5, c: 'Numbers class', a: 'MATH' }, { n: 7, c: 'Drawing class', a: 'ART' }], d: [{ n: 1, c: 'BPM', a: 'BPM' }] }),

    createPuzzle(17, "Water", [
        ['S', 'E', 'A', '', ''],
        ['L', 'A', 'K', 'E', ''],
        ['R', 'A', 'I', 'N', ''],
        ['I', 'C', 'E', '', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Ocean', a: 'SEA' }, { n: 3, c: 'Pond', a: 'LAKE' }, { n: 5, c: 'Sky water', a: 'RAIN' }, { n: 6, c: 'Frozen water', a: 'ICE' }], d: [{ n: 1, c: 'Lanka', a: 'SRI' }] }),

    createPuzzle(18, "Time", [
        ['D', 'A', 'Y', '', ''],
        ['W', 'E', 'E', 'K', ''],
        ['Y', 'E', 'A', 'R', ''],
        ['H', 'O', 'U', 'R', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: '24 hours', a: 'DAY' }, { n: 3, c: '7 days', a: 'WEEK' }, { n: 5, c: '365 days', a: 'YEAR' }, { n: 6, c: '60 minutes', a: 'HOUR' }], d: [{ n: 1, c: 'Moisture', a: 'DEW' }] }),

    createPuzzle(19, "Body", [
        ['H', 'E', 'A', 'D', ''],
        ['H', 'A', 'N', 'D', ''],
        ['F', 'O', 'O', 'T', ''],
        ['K', 'N', 'E', 'E', ''],
        ['', '', '', '', '']
    ], { a: [{ n: 1, c: 'Top part', a: 'HEAD' }, { n: 4, c: 'Fingers part', a: 'HAND' }, { n: 6, c: 'Toes part', a: 'FOOT' }, { n: 8, c: 'Leg joint', a: 'KNEE' }], d: [{ n: 2, c: 'End', a: 'END' }] }),

]

// Generate 100 puzzles by cycling existing ones
export const PUZZLES: Puzzle[] = Array.from({ length: 100 }, (_, i) => {
    const template = BASE_PUZZLES[i % BASE_PUZZLES.length]
    return {
        ...template,
        dayNumber: i,
        // Ensure ID is unique and formatted nicely
        id: `puz-${String(i).padStart(3, '0')}`
    }
})
